import cn from "classnames";
import { useState } from "react";
import { Button } from "@rshbintech.dboul/core-ui";
import {
  monolithApi,
  confirmationApi,
  MonolithApiTypes,
  ConfirmationApiTypes,
} from "@rshbintech.dboul/http";
import { useAppTranslation } from "../../hooks";
import { usePosition } from "../position-context";
import { SmsConfirmation } from "./sms-confirmation";
import { SIGNING_WITH_SMS_STATUSES } from "./constants";
import { SigningWithSmsProps, SigningWithSmsStatuses } from "./types";
import styles from "./styles.scss";

export const SigningWithSms = ({
  buttonText,
  className,
  documentId,
  onErrorSigning,
  onSuccessSigning,
  cryptoControlBlock,
  signingWithMonolith,
  onClickSigningButton,
  onClickSendSmsPasswordButton,
  messageTemplate,
  signedDocument,
  ccbVersion,
  disableSignButton,
  dataAttributesSigningWithSmsButton,
  dataAttributesSmsConfirmationButton,
}: SigningWithSmsProps) => {
  const [timer, setTimer] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [virtualCertificate, setVirtualCertificate] =
    useState<MonolithApiTypes.VirtualCertResponse | null>(null);

  const [isLoadingVirtualCertificate, setIsLoadingVirtualCertificate] =
    useState(false);

  const [signingStatus, setSigningStatus] = useState<SigningWithSmsStatuses>(
    SIGNING_WITH_SMS_STATUSES.BEGIN_SIGNING
  );

  const [attemptsCount, setAttemptsCount] = useState(0);
  const { translate } = useAppTranslation();

  const position = usePosition();

  const getVirtualCertificate = async () => {
    setSigningStatus(SIGNING_WITH_SMS_STATUSES.SEND_PASSWORD);

    if (
      !signingWithMonolith &&
      ccbVersion &&
      signedDocument &&
      messageTemplate
    ) {
      try {
        const confirmationCodeResponse =
          await confirmationApi.api.generateConfirmCode({
            document: signedDocument,
            ccbVersion,
            messageTemplate,
          });

        setAttemptsCount(confirmationCodeResponse.data.attemptsCount);
        setTimer(confirmationCodeResponse.data.validPeriodInSec);
        setIsTimerActive(true);
        setIsLoadingVirtualCertificate(false);
      } catch (error) {
        setIsTimerActive(false);
        setSigningStatus(SIGNING_WITH_SMS_STATUSES.BEGIN_SIGNING);
        setIsLoadingVirtualCertificate(false);
        onErrorSigning?.(
          (error as Record<string, ConfirmationApiTypes.ErrorMessage>)?.error
            ?.description
        ); // TODO: Изменить работу с типизацией и обработкой ошибок запросов
      }
    } else if (documentId && signingWithMonolith) {
      try {
        const virtualCertificateResponse =
          await monolithApi.certificates.virtualCertForSmsSignList({
            documentId: Number(documentId),
          });

        if (virtualCertificateResponse.data.timeBetweenResending) {
          setVirtualCertificate(virtualCertificateResponse.data);
          setTimer(virtualCertificateResponse.data.timeBetweenResending);
          setIsTimerActive(true);
          setIsLoadingVirtualCertificate(false);
        }
      } catch (error) {
        setIsTimerActive(false);
        setSigningStatus(SIGNING_WITH_SMS_STATUSES.BEGIN_SIGNING);
        setIsLoadingVirtualCertificate(false);
        onErrorSigning?.(
          (error as Record<string, MonolithApiTypes.ErrorResponse>)?.error
            ?.errors?.[0]
        ); // TODO: Изменить работу с типизацией и обработкой ошибок запросов
      }
    }
  };

  const sendSmsCode = () => {
    setIsLoadingVirtualCertificate(true);
    getVirtualCertificate();
    onClickSendSmsPasswordButton?.();
  };

  const isSmsPasswordSent =
    signingStatus === SIGNING_WITH_SMS_STATUSES.SEND_PASSWORD;

  return (
    <div
      data-test-id="signing-with-sms"
      className={cn(styles.container, className)}
    >
      {isSmsPasswordSent ? (
        <SmsConfirmation
          timer={timer}
          setTimer={setTimer}
          documentId={documentId}
          isTimerActive={isTimerActive}
          onErrorSigning={onErrorSigning}
          onSuccessSigning={onSuccessSigning}
          setIsTimerActive={setIsTimerActive}
          setSigningStatus={setSigningStatus}
          cryptoControlBlock={cryptoControlBlock}
          virtualCertificate={virtualCertificate}
          signingWithMonolith={signingWithMonolith}
          onClickSigningButton={onClickSigningButton}
          fetchVirtualCertificate={getVirtualCertificate}
          isLoadingVirtualCertificate={isLoadingVirtualCertificate}
          signedDocument={signedDocument}
          attemptsCount={attemptsCount}
          setAttemptsCount={setAttemptsCount}
          dataAttributesSmsConfirmationButton={
            dataAttributesSmsConfirmationButton
          }
        />
      ) : (
        <Button
          disabled={disableSignButton}
          onClick={sendSmsCode}
          dataTestId="send-sms-code-button"
          className={cn(styles["send-sms-code-button"], styles[position])}
          data-rptracker-event={
            dataAttributesSigningWithSmsButton?.dataRptrackerEvent
          }
          data-rptracker-category={
            dataAttributesSigningWithSmsButton?.dataRptrackerCategory
          }
          data-rptracker-action={
            dataAttributesSigningWithSmsButton?.dataRptrackerAction
          }
          data-rptracker-value={
            dataAttributesSigningWithSmsButton?.dataRptrackerValue
          }
        >
          {buttonText ?? translate("SIGNING_BUTTON_TEXT")}
        </Button>
      )}
    </div>
  );
};
